import { AssertEqualType } from '@lessonup/domain-utils';
import { z } from 'zod';
import { AnswerByCategoryPinSchema } from './answer-by-category-pin';
import { AnswerSettingSchema } from './base-student-pin';
import { DragAndDropPinSchema } from './drag-and-drop-pin';
import { FillInTheBlanksPinSchema } from './fill-in-the-blanks-pin';
import { NUMBER_OF_IMAGES_ALLOWED, OpenQuestionPinSchema } from './open-question-pin';
import { PinType } from './pin';
import { PollPinSchema } from './poll-pin';
import { QuizPinSchema, QuizStudentPinSettingsSchema } from './quiz-pin';
import { SlidePinSchema, SlideStudentPinSettingsSchema } from './slide-pin';
import { SortingQuestionPinSchema } from './sorting-question-pin';
import { UnsupportedPinSchema } from './unsupported-pin';
import { WordCloudPinSchema } from './word-cloud-pin';

export const QuizStudentPinSchema = QuizPinSchema.extend(
  z.object({
    studentSettings: QuizStudentPinSettingsSchema,
  }).shape
);

export type QuizStudentPin = z.infer<typeof QuizStudentPinSchema>;

export const SlideStudentPinSchema = SlidePinSchema.extend(
  z.object({
    studentSettings: SlideStudentPinSettingsSchema,
  }).shape
);

export type SlideStudentPin = z.infer<typeof SlideStudentPinSchema>;

export const OpenQuestionStudentPinSettingsSchema = AnswerSettingSchema.extend(
  z.object({
    maximumNumberOfUploads: z.enum(NUMBER_OF_IMAGES_ALLOWED),
  }).shape
);

export const OpenQuestionStudentPinSchema = OpenQuestionPinSchema.extend(
  z.object({
    studentSettings: OpenQuestionStudentPinSettingsSchema,
  }).shape
);
export type OpenQuestionStudentPin = z.infer<typeof OpenQuestionStudentPinSchema>;

// TODO: Add specific studentSettings for each placeholder
export const WordCloudStudentPinSchema = WordCloudPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);
export type WordCloudStudentPin = z.infer<typeof WordCloudStudentPinSchema>;

export const AnswerByCategoryStudentPinSchema = AnswerByCategoryPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);
export type AnswerByCategoryStudentPin = z.infer<typeof AnswerByCategoryStudentPinSchema>;

// TODO: @davey There's 3 variants of studentSettings Poll.Scale, Poll.Emoji, Poll.MultipleChoice
export const PollStudentPinSchema = PollPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);
export type PollStudentPin = z.infer<typeof PollStudentPinSchema>;

export const FillInTheBlanksStudentPinSchema = FillInTheBlanksPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);
export type FillInTheBlanksStudentPin = z.infer<typeof FillInTheBlanksStudentPinSchema>;

export const SortingQuestionStudentPinSchema = SortingQuestionPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);
export type SortingQuestionStudentPin = z.infer<typeof SortingQuestionStudentPinSchema>;

export const UnsupportedStudentPinSchema = UnsupportedPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);

export const DragAndDropStudentPinSchema = DragAndDropPinSchema.extend(
  z.object({
    studentSettings: z.object({}),
  }).shape
);
export type DragAndDropStudentPin = z.infer<typeof DragAndDropStudentPinSchema>;

export type UnsupportedStudentPin = z.infer<typeof UnsupportedStudentPinSchema>;

export const StudentPinSchema = z.discriminatedUnion('type', [
  QuizStudentPinSchema,
  SlideStudentPinSchema,
  OpenQuestionStudentPinSchema,
  WordCloudStudentPinSchema,
  AnswerByCategoryStudentPinSchema,
  PollStudentPinSchema,
  FillInTheBlanksStudentPinSchema,
  SortingQuestionStudentPinSchema,
  DragAndDropStudentPinSchema,
  UnsupportedStudentPinSchema,
]);

export type StudentPin = z.infer<typeof StudentPinSchema>;

// If this gives you an error, update the StudentPinSchema union
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ensureTypesAreInSync: AssertEqualType<PinType, StudentPin['type']> = true;
